import { IconDefinition, faCircleCheck, faCircleExclamation, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { type PropsWithChildren } from 'react';

import { Icon, cn } from '@growtherapy/sprout-ui';

import { ToastCloseButton } from './ToastCloseButton';
import { type ToastRenderFnProps, ToastVariant } from './types';

const VARIANT_TO_ICON: Record<ToastVariant, IconDefinition | null> = {
  [ToastVariant.Success]: faCircleCheck,
  [ToastVariant.Error]: faCircleExclamation,
  [ToastVariant.Info]: faCircleInfo,
};

export type ToastProps = PropsWithChildren<{
  /**
   * Control the alignment along the cross axis of the toast content.
   * Defaults to "center" for vertical alignment.
   */
  alignment?: 'top' | 'center';
  className?: string;
  /** Merged into the inner flex row that wraps the icon and children (after the icon). */
  contentClassName?: string;
  /** Provide closeButtonProps to show button for user to dismiss toast. */
  closeButtonProps?: ToastRenderFnProps['closeButtonProps'];
  closeButtonSize?: 'xs' | 'sm';
  ['data-testid']?: string;
  icon?: IconDefinition;
  variant?: ToastVariant;
}>;

// NOTE: This component is lifted from `client/src/Toast` and modified to
// integrate with @react-aria's toast ARIA props. Once the migration is
// complete, `Toast` will be totally superseded by this component.
/**
 * Renders a basic, styled toast container with the appropriate aria props and
 * an optional close button to dismiss the toast.
 */
export function Toast({
  alignment = 'center',
  children,
  className,
  contentClassName,
  closeButtonProps,
  closeButtonSize = 'xs',
  ['data-testid']: dataTestId,
  icon: iconProp,
  variant,
}: ToastProps) {
  const icon = iconProp ?? (variant ? VARIANT_TO_ICON[variant] : null);
  return (
    <div
      data-testid={dataTestId}
      className={cn(
        'bg-neutral-200 rebrand:bg-surface-emphasis',
        {
          'bg-green-100 rebrand:bg-status-success-surface': variant === ToastVariant.Success,
          'bg-coral-300 rebrand:bg-status-error-surface': variant === ToastVariant.Error,
        },
        {
          'items-center': alignment === 'center',
          'items-start': alignment === 'top',
        },
        'w-full sm:w-auto p-4 rounded-lg flex justify-between gap-3 border-[1px] border-neutral-800 border-solid shadow-md',
        'rebrand:p-250 rebrand:border-none rebrand:rounded-150-175',
        className,
      )}
    >
      <div
        className={cn(
          'flex grow justify-start gap-3 min-w-0',
          {
            'items-center': alignment === 'center',
            'items-start': alignment === 'top',
          },
          contentClassName,
        )}
      >
        {icon && (
          <Icon
            icon={icon}
            size='lg'
            className={cn('text-neutral-800', {
              'rebrand:text-status-success-text': variant === ToastVariant.Success,
              'rebrand:text-status-error-text': variant === ToastVariant.Error,
              'rebrand:text-text-default': variant === ToastVariant.Info,
            })}
          />
        )}
        {children}
      </div>
      {!!closeButtonProps && <ToastCloseButton {...closeButtonProps} size={closeButtonSize} variant={variant} />}
    </div>
  );
}
